import { UtilService } from 'common/services/utilService'

import type {
  IRoomOptionProduct,
  IRoomSelectedDay,
  IRoomSelectedTimeSlot,
} from 'features/Booking/interfaces/ILeaseInfoRoom'

const { structuredClone } = UtilService

const updateRoomOptions = (
  option: IRoomOptionProduct,
  options: IRoomOptionProduct[],
): IRoomOptionProduct[] => {
  let updatedOptions: IRoomOptionProduct[] = structuredClone<IRoomOptionProduct[]>(options)

  const optionIndex: number = options.findIndex(
    ({ id }: IRoomOptionProduct): boolean => id === option.id,
  )

  if (optionIndex === -1) updatedOptions.push(option)
  else updatedOptions.splice(optionIndex, 1)

  return updatedOptions
}

const updateDaySlots = (day: IRoomSelectedDay): IRoomSelectedDay => {
  const { selectedSlots: slots, selectedSlot: slot } = day
  const updatedSlots: IRoomSelectedTimeSlot[] = structuredClone<IRoomSelectedTimeSlot[]>(slots)

  if (slot) {
    const slotIndex: number = slots.findIndex(
      ({ id }: IRoomSelectedTimeSlot): boolean => id === slot.id,
    )
    if (slotIndex === -1) updatedSlots.push(slot)
    else updatedSlots.splice(slotIndex, 1, slot)
  }

  updatedSlots.sort((a: IRoomSelectedTimeSlot, b: IRoomSelectedTimeSlot) => a.id - b.id)

  return {
    ...day,
    selectedSlots: updatedSlots,
    selectedSlot: null,
  }
}

const filterOptionsByTypeService = (options: IRoomOptionProduct[] = []): IRoomOptionProduct[] =>
  options?.filter(({ type }: IRoomOptionProduct): boolean => type === 'service')

const getSlotsSubsequences = (slots: IRoomSelectedTimeSlot[]) => {
  let subsequences: IRoomSelectedTimeSlot[][] = []

  let uniqueSlots: IRoomSelectedTimeSlot[] = structuredClone<IRoomSelectedTimeSlot[]>(slots)
  uniqueSlots = [
    ...new Map(uniqueSlots.map((item: IRoomSelectedTimeSlot) => [item.id, item])).values(),
  ]

  uniqueSlots.forEach((slot: IRoomSelectedTimeSlot): void => {
    if (!!subsequences.length) {
      const lastSubsequence: IRoomSelectedTimeSlot[] = subsequences.at(-1)
      const lastSlotId: number = lastSubsequence.at(-1).id
      if (slot.id - lastSlotId === 1 || slot.id - lastSlotId === -1) {
        lastSubsequence.push(slot)
        subsequences.splice(-1, 1, lastSubsequence)
      } else subsequences.push([slot])
    } else subsequences.push([slot])
  })

  return subsequences
}

const filterOptionsByServices = (
  options: IRoomOptionProduct[],
  servicesMap: Map<number, number>,
): IRoomOptionProduct[] => {
  const updatedOptions: IRoomOptionProduct[] = options?.filter(
    (option: IRoomOptionProduct): boolean => {
      const serviceCount: number = servicesMap.get(option.id) || 0
      return serviceCount <= 1
    },
  )
  return updatedOptions
}

const updateSubsequenceSlot = (
  slot: IRoomSelectedTimeSlot,
  lastSlot: IRoomSelectedTimeSlot,
  servicesMap: Map<number, number>,
): IRoomSelectedTimeSlot => {
  if (slot.id < lastSlot.id) {
    return {
      ...slot,
      selectedOptions: filterOptionsByServices(slot.selectedOptions, servicesMap),
    }
  }
  return {
    ...lastSlot,
    selectedOptions: filterOptionsByServices(lastSlot.selectedOptions, servicesMap),
  }
}

const updateSubsequenceVisibility = (
  subsequence: IRoomSelectedTimeSlot[],
  servicesMap: Map<number, number>,
  lastSlot: IRoomSelectedTimeSlot,
): IRoomSelectedTimeSlot[] => {
  const updatedSubsequence: IRoomSelectedTimeSlot[] = subsequence.map(
    (slot: IRoomSelectedTimeSlot): IRoomSelectedTimeSlot => {
      if (!slot?.selectedOptions?.length) return updateSubsequenceSlot(slot, lastSlot, new Map())
      const slotServices: IRoomOptionProduct[] = filterOptionsByTypeService(slot?.selectedOptions)
      slotServices.forEach((option: IRoomOptionProduct): void => {
        if (servicesMap.has(option.id)) servicesMap.set(option.id, servicesMap.get(option.id) + 1)
        else servicesMap.set(option.id, 1)
      })
      return updateSubsequenceSlot(slot, lastSlot, servicesMap)
    },
  )

  return updatedSubsequence
}

const updateRoomServicePlacement = (day: IRoomSelectedDay): IRoomSelectedDay => {
  const { selectedSlots: slots } = day

  const copySlots: IRoomSelectedTimeSlot[] = structuredClone<IRoomSelectedTimeSlot[]>(slots)

  let subsequences: IRoomSelectedTimeSlot[][] = getSlotsSubsequences(copySlots)
  subsequences = subsequences.map((subsequence: IRoomSelectedTimeSlot[]) => {
    const servicesMap: Map<number, number> = new Map()
    const lastSlot: IRoomSelectedTimeSlot = structuredClone<IRoomSelectedTimeSlot>(
      subsequence.at(-1),
    )

    return updateSubsequenceVisibility(subsequence, servicesMap, lastSlot)
  })

  return { ...day, selectedSlots: subsequences.flat() }
}

export const LeaseBackToBackService = {
  updateDaySlots,
  updateRoomOptions,
  updateRoomServicePlacement,
}
